import { useMemo, useState } from 'react'

export interface FilterState {
  dateFrom: string
  dateTo: string
  q: string
}

export const EMPTY_FILTERS: FilterState = { dateFrom: '', dateTo: '', q: '' }

/**
 * Data calendaristica locala, NU prin `toISOString()`: acela trece in UTC, iar 1 august
 * la miezul noptii, ora Romaniei, devine 31 iulie. Toate intervalele iesau decalate cu o
 * zi — „Luna precedenta" arata 31-Jul … 30-Aug (Alexandru, 25.09.2026).
 */
function iso(d: Date): string {
  const dd = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`
}

/** Intervalele uzuale. „Toată perioada" înseamnă fără limite, nu un interval foarte larg. */
function presets(): { label: string; from: string; to: string }[] {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  return [
    { label: 'Luna curentă', from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    { label: 'Luna precedentă', from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    { label: 'Anul curent', from: `${y}-01-01`, to: `${y}-12-31` },
    { label: 'An precedent', from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
    { label: 'Toată perioada', from: '', to: '' },
  ]
}

const LUNI = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec']

/** Luna m (0–11) din anul y, de la prima la ultima zi. */
function monthRange(y: number, m: number): { from: string; to: string } {
  return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) }
}

const input =
  'w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green'

export function ListFilters({
  value,
  onChange,
  searchLabel = 'Caută',
  searchPlaceholder = 'număr document, comandă, client…',
}: {
  value: FilterState
  onChange: (v: FilterState) => void
  searchLabel?: string
  searchPlaceholder?: string
}) {
  const ps = useMemo(presets, [])
  const set = (patch: Partial<FilterState>) => onChange({ ...value, ...patch })
  const now = new Date()
  // Anul din selectorul de luni pornește de la intervalul ales, altfel anul curent.
  const [year, setYear] = useState(() => (value.dateFrom ? Number(value.dateFrom.slice(0, 4)) : now.getFullYear()))

  return (
    <div className="rounded-xl border border-b4l-border bg-white p-4">
      <div className="mb-2 text-[12px] font-medium text-b4l-text-muted">Filtre</div>

      <div className="mb-3 flex flex-wrap gap-2">
        {ps.map((p) => {
          const activ = value.dateFrom === p.from && value.dateTo === p.to
          return (
            <button
              key={p.label}
              onClick={() => set({ dateFrom: p.from, dateTo: p.to })}
              className={
                activ
                  ? 'rounded-md bg-b4l-green px-3 py-1 text-[12px] text-white'
                  : 'rounded-md border border-b4l-border px-3 py-1 text-[12px] text-b4l-text-muted hover:bg-b4l-bg-soft'
              }
            >
              {p.label}
            </button>
          )
        })}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-md border border-b4l-border text-[12px]">
          <button onClick={() => setYear(year - 1)} className="px-2 py-1 text-b4l-text-muted hover:bg-b4l-bg-soft" aria-label="Anul anterior">
            ‹
          </button>
          <span className="px-1 font-medium tabular-nums">{year}</span>
          <button
            onClick={() => setYear(year + 1)}
            disabled={year >= now.getFullYear()}
            className="px-2 py-1 text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-30"
            aria-label="Anul următor"
          >
            ›
          </button>
        </div>
        {LUNI.map((nume, m) => {
          const r = monthRange(year, m)
          const activ = value.dateFrom === r.from && value.dateTo === r.to
          const viitoare = year > now.getFullYear() || (year === now.getFullYear() && m > now.getMonth())
          return (
            <button
              key={nume}
              onClick={() => set({ dateFrom: r.from, dateTo: r.to })}
              disabled={viitoare}
              className={
                activ
                  ? 'w-11 rounded-md bg-b4l-green py-1 text-[12px] text-white'
                  : 'w-11 rounded-md border border-b4l-border py-1 text-[12px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-30 disabled:hover:bg-transparent'
              }
            >
              {nume}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-[12px] text-b4l-text-muted">De la</label>
          <input type="date" value={value.dateFrom} onChange={(e) => set({ dateFrom: e.target.value })} className={input} />
        </div>
        <div>
          <label className="mb-1 block text-[12px] text-b4l-text-muted">Până la</label>
          <input type="date" value={value.dateTo} onChange={(e) => set({ dateTo: e.target.value })} className={input} />
        </div>
        <div>
          <label className="mb-1 block text-[12px] text-b4l-text-muted">{searchLabel}</label>
          <input
            value={value.q}
            onChange={(e) => set({ q: e.target.value })}
            placeholder={searchPlaceholder}
            className={input}
          />
        </div>
      </div>
    </div>
  )
}
